import {
  SpaceBetween,
  FormField,
  Input,
  Textarea,
  SegmentedControl,
  Autosuggest,
  SegmentedControlProps,
  AutosuggestProps,
  Select,
  SelectProps,
} from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { useState, useRef } from 'react';
import { OptionalLabel } from './Common';
import { getEnabledFeatures, useFeatures } from '../../../../hooks/features';
import { loadLocationSuggestions } from '../../../../hooks/locations';
import { autocomplete, getPlace, geocode } from '../../../../clients/amazonLocationClient';
import { useSearchParams } from 'react-router-dom';
import { useTimezone } from '../../../../hooks/timezone';
import { EventInput, VCLocationType } from '@amzn/red-velvet-api';
import { PapiLocation } from '@amzn/red-velvet-api';

export interface LocationComponentProps {
  currentFields: EventInput;
  updateFieldHandler: (fields: EventInput) => void;
  errors?: Record<string, string>;
  isReadOnly?: boolean;
}

const LOCATION_AUTO_COMPLETE_MIN_CHAR = 2;
const ROOM_INPUT_MAX_CHAR = 30;
const LOCATION_NAME_MAX_CHAR = 100;

export function LocationComponent(props: LocationComponentProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const locationTypeOption: Record<string, SegmentedControlProps.Option> = {
    ON_CAMPUS: {
      text: t('eventDetailsStep.onCampus'),
      id: VCLocationType.ON_CAMPUS,
      disabled: props.isReadOnly,
    },
    OFF_CAMPUS: {
      text: t('eventDetailsStep.offCampus'),
      id: VCLocationType.OFF_CAMPUS,
      disabled: props.isReadOnly,
    },
    VIRTUAL: {
      text: t('eventDetailsStep.virtual'),
      id: VCLocationType.VIRTUAL,
      disabled: props.isReadOnly,
    },
  };

  const [locationType, setLocationType] = useState(
    locationTypeOption[props.currentFields.locationType ?? VCLocationType.ON_CAMPUS]
  );
  const [locationVirtualNotes, setLocationVirtualNotes] = useState(
    props.currentFields.locationVirtualNotes ?? ''
  );
  const [locationNotes, setLocationNotes] = useState(props.currentFields.location?.notes ?? '');
  // Additional Fields for Off campus location
  const [location, setLocation] = useState(props.currentFields.location?.address_string ?? '');
  const [locationName, setLocationName] = useState(props.currentFields.location?.name ?? '');
  // Additional Fields for On campus location
  const [building, setBuilding] = useState(props.currentFields.location?.amazonBuilding);
  const [room, setRoom] = useState(props.currentFields.location?.amazonBuildingRoom ?? '');
  // Timezone for virtual events
  const timezoneOptions = t('timezones', { returnObjects: true }) as SelectProps.Options;
  const userTimezone = useTimezone();
  const findTimezoneOption = (tz: string): SelectProps.Option | null => {
    for (const group of timezoneOptions) {
      const match = (group as SelectProps.OptionGroup).options?.find((o) => o.value === tz);
      if (match) return match;
    }
    return null;
  };
  const [timezone, setTimezone] = useState<SelectProps.Option | null>(() => {
    const saved = props.currentFields.timeZone;
    return findTimezoneOption(saved ?? userTimezone);
  });

  const [options, setOptions] = useState<AutosuggestProps.Options>([]);
  const [status, setStatus] = useState<AutosuggestProps.StatusType>();
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  const fieldsRef = useRef(props.currentFields);
  fieldsRef.current = props.currentFields;

  const placeIdMap = useRef(new Map<string, string>());
  const papiLocationMap = useRef(new Map<string, PapiLocation>());

  const resolveOffCampusPlace = async (placeId: string, label: string) => {
    try {
      const response = await getPlace({ PlaceId: placeId, IntendedUse: 'Storage' });
      console.log('Off-campus getPlace response:', response);
      if (response.Position) {
        props.updateFieldHandler({
          ...fieldsRef.current,
          location: {
            ...fieldsRef.current.location,
            latitude: response.Position[1],
            longitude: response.Position[0],
            address_string: response.Title ?? label,
            street: response.Address?.Street,
            city: response.Address?.Locality,
            province: response.Address?.Region?.Name,
            country: response.Address?.Country?.Name,
            postal_code: response.Address?.PostalCode,
          },
        });
      }
    } catch (e) {
      console.error('Failed to resolve off-campus place:', e);
    }
  };

  const resolveOnCampusBuilding = async (papiLocation: PapiLocation) => {
    try {
      const addr = papiLocation.address;
      const queryText = [addr.unifiedAddressLine, addr.city, addr.region, addr.country]
        .filter(Boolean)
        .join(', ');
      const response = await geocode({ QueryText: queryText, IntendedUse: 'Storage' });
      console.log('On-campus geocode response:', response);
      const result = response.ResultItems?.[0];
      if (result?.Position) {
        props.updateFieldHandler({
          ...fieldsRef.current,
          location: {
            ...fieldsRef.current.location,
            latitude: result.Position[1],
            longitude: result.Position[0],
            address_string: result.Title ?? queryText,
            street: result.Address?.Street,
            city: result.Address?.Locality,
            province: result.Address?.Region?.Name,
            country: result.Address?.Country?.Name,
            postal_code: result.Address?.PostalCode,
          },
        });
      }
    } catch (e) {
      console.error('Failed to geocode on-campus building:', e);
    }
  };

  const resetLocationState = () => {
    // Resetting all of the additional fields state if the location type field change
    setOptions([]);
    setStatus(undefined);
    setBuilding('');
    setRoom('');
    setLocation('');
    setLocationName('');
    setLocationVirtualNotes('');
    setLocationNotes('');
    setTimezone(null);
  };

  const loadLocationOptions = async (
    filteringText: string,
    eventType: 'on-campus' | 'off-campus' | 'virtual'
  ) => {
    // Location autocomplete api only loads with 2+ characters
    if (filteringText.length >= LOCATION_AUTO_COMPLETE_MIN_CHAR) {
      try {
        setStatus('loading');
        setOptions([]);

        let locationOptions: AutosuggestProps.Option[];
        if (eventType === 'on-campus') {
          // On-campus: use PAPI for Amazon building code lookup
          const result = await loadLocationSuggestions(
            filteringText,
            eventType,
            getEnabledFeatures(features, searchParams)
          );
          locationOptions = result.options;
          papiLocationMap.current = result.papiLocations;
        } else {
          // Off-campus: use Amazon Location Service autocomplete
          const response = await autocomplete({
            QueryText: filteringText,
            MaxResults: 10,
          });
          placeIdMap.current.clear();
          locationOptions = (response.ResultItems ?? []).map((item) => {
            const value = item.Title ?? '';
            if (item.PlaceId) placeIdMap.current.set(value, item.PlaceId);
            return { value, label: value, description: item.PlaceType };
          });
        }

        setOptions(locationOptions);
        setStatus('finished');
      } catch (e) {
        setStatus('error');
      }
    } else {
      setOptions([]);
    }
  };

  const renderOnCampusQuestion = () => {
    return (
      <SpaceBetween direction="vertical" size="l">
        <FormField
          label={t('eventDetailsStep.building')}
          description={t('eventDetailsStep.buildingSubText')}
          errorText={props.errors?.building}
        >
          <Select
            selectedOption={building ? { value: building, label: building } : null}
            onChange={(event) => {
              const selected = event.detail.selectedOption;
              setBuilding(selected.value);
              props.updateFieldHandler({
                ...props.currentFields,
                location: {
                  ...props.currentFields.location,
                  amazonBuilding: selected.value,
                  name: room?.trim() ? `${selected.value} - ${room}` : selected.value,
                },
              });
              const papiLocation = papiLocationMap.current.get(selected.value ?? '');
              if (papiLocation) {
                resolveOnCampusBuilding(papiLocation);
              }
            }}
            placeholder={t('eventDetailsStep.buildingPlaceholder')}
            statusType={status}
            options={options}
            empty={t('additionalInfo.noLocationSuggestions')}
            loadingText={t('additionalInfo.loadingLocationSuggestions')}
            onLoadItems={(event) => {
              loadLocationOptions(event.detail.filteringText, 'on-campus');
            }}
            filteringType="manual"
            virtualScroll
            readOnly={props.isReadOnly}
          />
        </FormField>
        <FormField
          label={t('eventDetailsStep.room')}
          description={t('eventDetailsStep.roomSubText')}
          errorText={
            props.errors?.room ??
            (room?.length >= ROOM_INPUT_MAX_CHAR
              ? t('error.inputTooLong', { maxLength: ROOM_INPUT_MAX_CHAR })
              : '')
          }
        >
          <Input
            placeholder={t('eventDetailsStep.roomPlaceholder')}
            onChange={(event) => {
              const roomInput = event.detail.value;
              setRoom(roomInput);
              props.updateFieldHandler({
                ...props.currentFields,
                location: {
                  ...props.currentFields.location,
                  name: building?.trim() ? `${building} - ${roomInput}` : roomInput,
                  amazonBuildingRoom: roomInput,
                },
              });
            }}
            value={room}
            readOnly={props.isReadOnly}
          />
        </FormField>
        <FormField
          label={
            <SpaceBetween direction="horizontal" size="l">
              <OptionalLabel>{t('eventDetailsStep.locationVirtualNotes')}</OptionalLabel>
            </SpaceBetween>
          }
          description={t('eventDetailsStep.locationVirtualNotesSubText')}
        >
          <Textarea
            onChange={(event) => {
              setLocationNotes(event.detail.value);
              props.updateFieldHandler({
                ...props.currentFields,
                location: {
                  ...props.currentFields.location,
                  notes: event.detail.value,
                },
              });
            }}
            value={locationNotes}
            placeholder={t('eventDetailsStep.locationVirtualNotesPlaceholder')}
            readOnly={props.isReadOnly}
          />
        </FormField>
      </SpaceBetween>
    );
  };

  const renderOffCampusQuestion = () => {
    return (
      <SpaceBetween direction="vertical" size="l">
        <FormField
          label={t('eventDetailsStep.location')}
          description={t('eventDetailsStep.locationSubText')}
          errorText={props.errors?.location}
        >
          <Autosuggest
            value={location}
            enteredTextLabel={(value) => value}
            onChange={(event) => {
              setLocation(event.detail.value || '');
            }}
            onSelect={({ detail }) => {
              const placeId = placeIdMap.current.get(detail.value);
              if (placeId) {
                resolveOffCampusPlace(placeId, detail.value);
              }
            }}
            placeholder={t('eventDetailsStep.locationPlaceholder')}
            statusType={status}
            options={options}
            empty={t('additionalInfo.noLocationSuggestions')}
            loadingText={t('additionalInfo.loadingLocationSuggestions')}
            onLoadItems={(event) => {
              loadLocationOptions(event.detail.filteringText, 'off-campus');
            }}
            virtualScroll
            readOnly={props.isReadOnly}
          />
        </FormField>
        <FormField
          label={t('eventDetailsStep.locationName')}
          description={t('eventDetailsStep.locationNameSubText')}
          errorText={
            props.errors?.locationName ??
            (locationName.length >= LOCATION_NAME_MAX_CHAR
              ? t('error.inputTooLong', { maxLength: LOCATION_NAME_MAX_CHAR })
              : '')
          }
        >
          <Input
            value={locationName}
            placeholder={t('eventDetailsStep.locationNamePlaceholder')}
            readOnly={props.isReadOnly}
            onChange={(event) => {
              setLocationName(event.detail.value);
              props.updateFieldHandler({
                ...props.currentFields,
                location: {
                  ...props.currentFields.location,
                  name: event.detail.value,
                },
              });
            }}
          />
        </FormField>
        <FormField
          label={
            <SpaceBetween direction="horizontal" size="l">
              <OptionalLabel>{t('eventDetailsStep.locationVirtualNotes')}</OptionalLabel>
            </SpaceBetween>
          }
          description={t('eventDetailsStep.locationVirtualNotesSubText')}
        >
          <Textarea
            onChange={(event) => {
              setLocationNotes(event.detail.value);
              props.updateFieldHandler({
                ...props.currentFields,
                location: {
                  ...props.currentFields.location,
                  notes: event.detail.value,
                },
              });
            }}
            value={locationNotes}
            placeholder={t('eventDetailsStep.locationVirtualNotesPlaceholder')}
            readOnly={props.isReadOnly}
          />
        </FormField>
      </SpaceBetween>
    );
  };

  const renderVirtualQuestion = () => {
    return (
      <SpaceBetween direction="vertical" size="l">
        <FormField
          label={t('eventDetailsStep.eventTimezone')}
          description={t('eventDetailsStep.eventTimezoneSubText')}
          errorText={props.errors?.timeZone}
        >
          <Select
            selectedOption={timezone}
            onChange={(event) => {
              setTimezone(event.detail.selectedOption);
              props.updateFieldHandler({
                ...props.currentFields,
                timeZone: event.detail.selectedOption.value,
              });
            }}
            options={timezoneOptions}
            placeholder={t('eventDetailsStep.eventTimezonePlaceholder')}
            filteringType="auto"
            readOnly={props.isReadOnly}
          />
        </FormField>
        <FormField
          label={<OptionalLabel>{t('eventDetailsStep.virtualNotes')}</OptionalLabel>}
          description={t('eventDetailsStep.locationVirtualNotesSubText')}
        >
          <Textarea
            onChange={(event) => {
              setLocationVirtualNotes(event.detail.value);
              props.updateFieldHandler({
                ...props.currentFields,
                locationVirtualNotes: event.detail.value,
              });
            }}
            value={locationVirtualNotes}
            placeholder={t('eventDetailsStep.virtualNotesPlaceholder')}
            readOnly={props.isReadOnly}
          />
        </FormField>
      </SpaceBetween>
    );
  };

  const renderAdditionalQuestion = () => {
    switch (locationType.id) {
      case VCLocationType.ON_CAMPUS:
        return renderOnCampusQuestion();
      case VCLocationType.OFF_CAMPUS:
        return renderOffCampusQuestion();
      case VCLocationType.VIRTUAL:
        return renderVirtualQuestion();
      default:
        return;
    }
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      <FormField
        label={t('eventDetailsStep.locationType')}
        description={t('eventDetailsStep.locationTypeSubText')}
      >
        <SegmentedControl
          selectedId={locationType.id}
          onChange={(event) => {
            const selectedType = event.detail.selectedId as VCLocationType;
            setLocationType(locationTypeOption[event.detail.selectedId]);
            resetLocationState();
            const defaultTz = findTimezoneOption(userTimezone);
            if (selectedType === VCLocationType.VIRTUAL && defaultTz) {
              setTimezone(defaultTz);
            }
            props.updateFieldHandler({
              ...props.currentFields,
              locationType: selectedType,
              // Clear location-related fields when location type changes
              location: undefined,
              locationVirtualNotes: '',
              timeZone: selectedType === VCLocationType.VIRTUAL ? defaultTz?.value : undefined,
            });
          }}
          options={[
            locationTypeOption[VCLocationType.ON_CAMPUS],
            locationTypeOption[VCLocationType.OFF_CAMPUS],
            locationTypeOption[VCLocationType.VIRTUAL],
          ]}
        />
      </FormField>
      {renderAdditionalQuestion()}
    </SpaceBetween>
  );
}
